import React, { useContext, useState, useRef, useEffect, Suspense } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Menu, LogOut, ChevronDown, Building2 } from "lucide-react";
import ResidentSidebar from "../components/ResidentSidebar";
import { LogoIcon, LogoWordmark } from "../components/brand/Logo";

const getInitials = (name: string) => {
  const p = name.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0].slice(0, 2).toUpperCase();
};

const ResidentLayout: React.FC = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropOpen, setDropOpen]     = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    setDropOpen(false);
    logout();
    navigate("/login");
  };

  const initials = user?.name ? getInitials(user.name) : "R";

  return (
    <div className="flex min-h-screen bg-slate-50">

      {/* ── Desktop sidebar ──────────────────────────────────────────────────── */}
      <div className="hidden md:flex md:shrink-0 sticky top-0 h-screen self-start">
        <ResidentSidebar />
      </div>

      {/* ── Mobile drawer ────────────────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="relative z-50">
            <ResidentSidebar onClose={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* ── Right column ─────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <header className="bg-white border-b border-slate-100 sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center justify-between shrink-0">

          {/* Mobile: hamburger */}
          <button
            className="md:hidden p-2 rounded-xl hover:bg-slate-100 text-slate-500"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>

          {/* Mobile: brand */}
          <div className="md:hidden flex items-center gap-2">
            <LogoIcon light={false} className="h-7 w-auto" />
            <LogoWordmark light={false} className="h-5 w-auto" />
          </div>

          {/* Desktop: spacer so avatar stays right */}
          <div className="hidden md:block flex-1" />

          {/* Avatar dropdown (both breakpoints) */}
          <div className="relative" ref={dropRef}>
            <button
              onClick={() => setDropOpen((v) => !v)}
              className="flex items-center gap-2.5 py-1.5 px-2.5 rounded-2xl hover:bg-slate-50 transition-all"
              aria-expanded={dropOpen}
            >
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold shrink-0 ring-2 ring-blue-100">
                {initials}
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-xs font-bold text-slate-700 leading-none">{user?.name || "Resident"}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{user?.tenantName}</div>
              </div>
              <ChevronDown size={13} className={`text-slate-400 transition-transform ${dropOpen ? "rotate-180" : ""}`} />
            </button>

            {dropOpen && (
              <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50">
                {/* Header */}
                <div className="px-4 py-4 bg-gradient-to-br from-blue-600 to-blue-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white leading-tight truncate">{user?.name || "Resident"}</div>
                      <div className="text-[10px] text-blue-200 truncate">{user?.email || ""}</div>
                    </div>
                  </div>
                </div>
                {/* Society */}
                <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                  <Building2 size={12} className="text-blue-500 shrink-0" />
                  <span className="text-xs text-blue-700 font-bold truncate">{user?.tenantName}</span>
                </div>
                {/* Logout */}
                <div className="py-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-semibold"
                  >
                    <LogOut size={15} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6">
          <Suspense fallback={<div className="flex items-center justify-center py-24"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
};

export default ResidentLayout;
